import { gql, useMutation } from '@apollo/client'
import { Button, DownloadIcon, useToast } from '@farol-ds/react'
import {
  LEGAL_RESEARCH_SUCCESS_PROXIES,
  useGADispatcher,
  useMetrics,
} from '@jusbrasil-web/docview/core'
import { LoginTrigger } from '@jusbrasil-web/shared-bifrost'
import { useBoxModal } from '@jusbrasil-web/shared-box-lock'

import {
  ConvertDocumentButtonBase_DocumentFragment,
  ConvertDocumentButtonBase_ViewerFragment,
} from './__generated__/convert-document-button-base.graphql'
import { BoxProps } from './types'

export interface ConvertDocumentButtonBaseProps {
  document: ConvertDocumentButtonBase_DocumentFragment
  viewer: ConvertDocumentButtonBase_ViewerFragment | null
  onClick?: (successfulAttempt: boolean) => void
  boxProps: BoxProps
  ga4EventLabel: string
  disabled?: boolean
  viewerCanDownload: boolean | null
}

export const ConvertDocumentMutation = gql`
  mutation convertDocumentMutation($input: ConvertDocumentInput!) {
    convertDocument(input: $input) {
      signedUrl
      metadata
    }
  }
`

const conversionPerformanceMarks = ['conversion-start', 'conversion-end']

export function ConvertDocumentButtonBase(props: ConvertDocumentButtonBaseProps) {
  const {
    document,
    viewer,
    viewerCanDownload,
    onClick,
    boxProps,
    ga4EventLabel,
    disabled = false,
  } = props
  const { docId, artifact } = document
  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLButtonElement>()
  const [getDownloadUrl, { loading }] = useMutation(ConvertDocumentMutation)
  const toast = useToast()
  const { setOpen } = useBoxModal({
    appId: 'web-docview',
    boxOptions: {},
    pageMetadata: {},
    viewer,
    enabled: !viewerCanDownload,
    ...boxProps,
  })
  const { sendGAEvent } = useGADispatcher()
  const metricsProperties = {
    feature_code: boxProps.featureCode,
    from_component: boxProps.fromComponent,
  }

  const getConversionDurationMs = () => {
    performance.mark(conversionPerformanceMarks[1])
    return performance.measure('conversion-timing', ...conversionPerformanceMarks).duration
  }

  const handleDownloadError = (
    error: Error | null,
    conversionMetadata: Record<string, unknown> = {}
  ) => {
    toast('Ocorreu um erro ao converter esse documento', { type: 'negative' })
    const metadata: Record<string, unknown> = {
      conversion: {
        ...conversionMetadata,
        durationMs: getConversionDurationMs(),
      },
    }
    if (error) {
      metadata.error = String(error)
    }
    onClick?.(false)
    sendMetrics('Doc.FileConversionError', {
      ...metricsProperties,
      metadata,
    })
  }

  const handleClick = () => {
    performance.clearMarks()
    performance.clearMeasures()
    performance.mark(conversionPerformanceMarks[0])

    sendMetrics('Doc.FileConversionClicked', metricsProperties)
    sendGAEvent(LEGAL_RESEARCH_SUCCESS_PROXIES, 'download', ga4EventLabel, 0)

    if (!viewer) {
      onClick?.(false)
      return
    }

    const hasPendingPayment = !!viewer.subscriptions?.some((s) => s?.overdue)
    if (hasPendingPayment || !viewerCanDownload) {
      onClick?.(false)
      setOpen(true)
      return
    }

    getDownloadUrl({
      variables: { input: { docId, artifact, fromComponent: boxProps.fromComponent } },
    })
      .then(({ data }) => {
        if (data?.convertDocument?.signedUrl) {
          window.open(data.convertDocument.signedUrl, '_self')

          onClick?.(true)
          sendMetrics('Doc.FileConverted', {
            ...metricsProperties,
            metadata: {
              conversion: {
                ...data.convertDocument.metadata,
                durationMs: getConversionDurationMs(),
              },
            },
          })
        } else {
          handleDownloadError(null, data?.convertDocument?.metadata)
          onClick?.(false)
        }
      })
      .catch(handleDownloadError)
  }

  return (
    <LoginTrigger
      isLogged={!!viewer}
      options={{
        eventData: {
          feature_code: boxProps.featureCode,
          from_component: boxProps.fromComponent,
        },
      }}
    >
      <Button
        kind="primary"
        leftIcon={<DownloadIcon />}
        onClick={handleClick}
        ref={dispatcherRef}
        loading={loading}
        disabled={disabled}
      >
        Baixar
      </Button>
    </LoginTrigger>
  )
}

ConvertDocumentButtonBase.fragments = {
  document: gql`
    fragment ConvertDocumentButtonBase_document on Document {
      docId
      artifact
    }
  `,
  viewer: gql`
    fragment ConvertDocumentButtonBase_viewer on Profile {
      ...useBoxModal_viewer
      subscriptions {
        overdue
      }
    }
    ${useBoxModal.fragments.viewer}
  `,
}
